import type { Insurance, IShipment, Product } from "~/types/shipment";

/**
 * Formats extra services for API submission
 */
export const formatExtraServices = (values: Record<string, any>): Record<string, any> => {
  const extraservices: Record<string, any> = {
    service_codes: [],
  };

  Object.entries(values).forEach(([key, value]: [string, any]) => {
    if (!value.isSet) {
      return;
    }

    if (/^([A-Z0-9]{3})$/.test(key)) {
      extraservices.service_codes.push(key);
    } else if (value.array) {
      extraservices[key] = value.array;
    } else {
      extraservices[key] = value;
    }
  });

  return extraservices;
};

/**
 * Transform a sender or receiver object to the standard format
 */
export const transformReceiverSender = (receiverSender: any): Record<string, any> => {
  if (!receiverSender) return {};

  return {
    city: receiverSender.city,
    country: receiverSender.country,
    email: receiverSender.email,
    name: receiverSender.name,
    attention: receiverSender.attention,
    phone: (receiverSender.phone_prefix || "") + "" + (receiverSender.phone || ""),
    street1: receiverSender.street1,
    street2: receiverSender.street2,
    zip_code: receiverSender.zip_code,
    business: receiverSender.business,
  };
};

/**
 * Set insurance values on the modal
 */
export const setInsuranceValues = (
  setValues: (values: Record<string, any>) => void,
  insurance: Insurance,
  accountIsBusiness: boolean
): void => {
  setValues({
    // No id means the carrier's included cover - there is no product to buy, so it books as 0.
    insurance: insurance.id ?? 0,
    insurance_coverage: insurance.coverage,
    insurance_price: accountIsBusiness ? insurance.price_excl_tax : insurance.price_incl_tax,
    // The currency the amounts above are in, as labelled by the API. Never assume DKK.
    insurance_currency: insurance.currency,
  });
};

/**
 * Process products after they are fetched
 */
export const processProducts = (
  setValues: (values: Record<string, any>) => void,
  products: Product[],
  shipment: IShipment,
  accountIsBusiness: boolean
): void => {
  if (!products || !products.length) return;

  const shipmentCps = `${shipment.carrier}_${shipment.carrier_product}_${shipment.carrier_service}`;
  const selectedProduct = products.find((product: Product) => product.carrier_product_service === shipmentCps);

  if (!selectedProduct) return;

  setValues({
    cpsUid: selectedProduct.uid,
    carrier_product_service: selectedProduct.carrier_product_service,
    carrier_product_title: selectedProduct.title,
    carrier_product_price: accountIsBusiness
      ? selectedProduct.sales_price_excl_vat
      : selectedProduct.sales_price_incl_vat,
    carrier_product_currency: selectedProduct.currency,
  });
};

/**
 * Prepare a shipment API payload from form data
 */
export const prepareShipmentPayload = (
  formData: Record<string, any>,
  billingInfo: any,
  formatDate: Function
): Record<string, any> => {
  // Parse carrier product service
  const cps = formData.carrier_product_service.split("_");
  const [carrier, carrier_product, ...carrier_service] = cps;

  // Handle pickup datetime
  let pickup_datetime = null;
  if (formData.pickup_date) {
    const pickup_time = new Date(formData.pickup_time);
    const pickup_date = new Date(
      new Date(formData.pickup_date).setHours(pickup_time.getHours(), pickup_time.getMinutes())
    );
    pickup_datetime = formatDate(pickup_date, { format: "yyyy-MM-dd HH:mm:ss" });
  } else if (carrier === "dfm") {
    pickup_datetime = formatDate(formData.pickup_time, { format: "yyyy-MM-dd HH:mm:ss" });
  }

  // Handle order lines
  if (formData.order_lines) {
    if (!Array.isArray(formData.order_lines)) {
      formData.order_lines = Object.values(formData.order_lines);
    }

    formData.order_lines = formData.order_lines.map((line: any) => {
      let updatedCustoms = line.customs || [];
      if (Array.isArray(line.customs)) {
        updatedCustoms = line.customs.map((custom: any) => ({
          ...custom,
          sender_tariff: String(custom.sender_tariff),
          receiver_tariff: String(custom.receiver_tariff),
        }));
      } else if (typeof line.customs === "object" && line.customs !== null) {
        updatedCustoms = line.customs;
      }

      if (!line.dangerous_goods_set && line.dangerous_goods?.length) {
        line.dangerous_goods = [];
      }

      return {
        ...line,
        customs: updatedCustoms,
      };
    });
  }

  // Build request payload
  return {
    shipment: {
      sender: {
        ...formData.sender,
        country: formData.sender.country.toUpperCase(),
        type: formData.sender.business ? "business" : "private",
        name: formData.sender.name,
        attention: formData.sender.attention,
      },
      receiver: {
        ...formData.receiver,
        country: formData.receiver.country.toUpperCase(),
        type: formData.receiver.business ? "business" : "private",
        name: formData.receiver.name,
        attention: formData.receiver.attention,
      },
      height: formData.height,
      length: formData.length,
      width: formData.width,
      weight: formData.weight,
      carrier,
      carrier_product,
      carrier_service: carrier_service.join("_"),
      servicepoint_id: formData.droppoint,
      insurance: formData.insurance,
      reference: formData.reference,
      order_lines: formData.set_order_lines || formData.order_lines || [],
      meta: {
        billing_info: billingInfo,
      },
      services: formatExtraServices(formData.services || {}),
      pickup_date: pickup_datetime,
      comment: formData.comment,
      original_shipment_id: formData.original_shipment_id,
    },
    final_url: window.location.origin + "/redirects/order/success",
  };
};

/**
 * Save a contact (receiver or sender)
 */
export const saveContact = async (contact: Record<string, any>): Promise<void> => {
  return homeFetch("contacts", {
    method: "POST",
    body: {
      company_name: contact.business ? contact.name : null,
      name: !contact.attention ? contact.name : contact.attention,
      is_business: contact.business,
      email: contact.email,
      phone: contact.phone,
      phone_with_prefix: contact.phone,
      street1: contact.street1,
      street2: contact.street2,
      region: contact.state || null,
      zip_code: contact.zip_code,
      city: contact.city,
      country: contact.country,
    },
  })
    .then(() => {
      return Promise.resolve();
    })
    .catch((error) => {
      console.error("Error saving contact:", error);
    });
};
